/**
 * API クライアント。
 *
 * ID トークンの付与とエラー形式の解釈を1か所に集約する。画面側は例外の `message` を
 * そのまま利用者に見せてよい（サーバーが利用者向けの文言だけを返すため）。
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export interface ApiErrorDetail {
  field?: string;
  code: string;
  message: string;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

type TokenProvider = () => Promise<string | null>;

let getToken: TokenProvider = async () => null;
let onUnauthenticated: () => void = () => {};

export function configureApi(options: {
  getToken: TokenProvider;
  onUnauthenticated: () => void;
}): void {
  getToken = options.getToken;
  onUnauthenticated = options.onUnauthenticated;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await getToken();
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text === '' ? null : JSON.parse(text);

  if (!response.ok) {
    const error = (
      payload as { error?: { code?: string; message?: string; details?: ApiErrorDetail[] } }
    )?.error;
    if (response.status === 401) onUnauthenticated();
    throw new ApiRequestError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? '通信に失敗しました。時間をおいてやり直してください。',
      error?.details ?? [],
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body ?? {}),
  delete: <T>(path: string): Promise<T> => request<T>('DELETE', path),
};
